'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeProps } from '@sola/ark-core/atoms/badge/badge.tsx';
import { NumberField } from '@sola/ark-core/molecules/number-field/number-field.tsx';
import { RadioGroup, RadioItemProps } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { BadgeColors, FundOptionProps } from '@sola/schema/lib/schema.ts';
import { camelCase } from '@sola/utils/string.ts';
import { FundQueryParams } from '../../../../utils/query-params.tsx';
import { useDonate } from '../donate-context.tsx';
import { DonateActionEnum, DonateFrequency } from '../donate.types.ts';
import { useDonateActions } from '../hooks/use-donate-actions.tsx';
import { useDonateValidation } from '../hooks/use-donate-validation.tsx';
export const DonateAmountSelector: React.FC<{
  className?: string;
  focusOnLoad?: boolean;
  alt?: boolean;
}> = ({
  className,
  focusOnLoad = false,
  alt
}) => {
  const {
    t
  } = useTranslation(['core', 'donations', 'skippy']);
  const {
    config,
    state,
    props,
    variant,
    fundParams,
    refs,
    dispatch
  } = useDonate();
  const {
    getCurrentAmount
  } = useDonateValidation();
  const {
    handleAmountSelect,
    handleOtherAmountChange,
    handleInputFocus,
    handleInputBlur
  } = useDonateActions();
  const getRadioItemsForFrequency = (frequency: DonateFrequency): RadioItemProps[] => {
    const freq = camelCase(frequency) as keyof FundQueryParams;
    const defaultFreq = `default${frequency}` as keyof FundQueryParams;
    const suggestedAmounts = (fundParams?.[freq] as number[])?.filter(v => v >= config.donationLimits.min && v <= config.donationLimits.max)?.map(v_0 => {
      return {
        value: v_0,
        isDefault: v_0 === fundParams?.[defaultFreq]
      } as FundOptionProps;
    }) ?? props.fund?.[`suggestedAmounts${frequency}`] ?? [];
    const items: RadioItemProps[] = [];
    if (suggestedAmounts.length > 1) {
      suggestedAmounts.forEach(amount => {
        items.push({
          value: amount.value.toString(),
          text: t('numbers.currencyCustom', {
            amount: amount.value
          }),
          subText: variant === 'grid' && amount.label ? amount.label : undefined,
          badge: amount.badge ? {
            label: amount.badge,
            color: 'positive' as BadgeColors
          } as BadgeProps : undefined,
          type: 'default'
        });
      });
    }
    items.push({
      value: 'other',
      type: 'other',
      text: t('donations:other'),
      element: <NumberField key={`${frequency}-other`} inputRef={refs[frequency]} name={frequency} aria-label={t('donations:other')} placeholder="0" onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOtherAmountChange(e.target.value)} value={state.otherAmounts[frequency]} onFocus={handleInputFocus} onBlur={handleInputBlur} prefix="$" />
    });
    return items;
  };
  const items_0 = getRadioItemsForFrequency(state.donateFrequency);
  if (!items_0.length) {
    return null;
  }
  const suggestedCount = (fundParams?.[camelCase(state.donateFrequency) as keyof FundQueryParams] as number[])?.length || props.fund?.[`suggestedAmounts${state.donateFrequency}`]?.length || 0;
  const suggestedAmountOnly = config.enabledFrequencies.length === 0 || suggestedCount < 2;
  return <div className={`DonateAmountSelector ${alt ? 'flex-1' : ''} ${className || ''}`}>
      {alt && <div className="Donate__other-input">
          <NumberField name="multiple-other" label={t('donations:amount')} placeholder="0" value={state.otherAmounts[state.donateFrequency]} onFocus={handleInputFocus} onBlur={handleInputBlur} onChange={(e_0: React.ChangeEvent<HTMLInputElement>) => {
        state.otherAmounts.Monthly = e_0.target.value;
        state.otherAmounts.OneTime = e_0.target.value;
        dispatch({
          type: DonateActionEnum.SET_OTHER_AMOUNT,
          payload: {
            frequency: state.donateFrequency,
            amount: e_0.target.value
          }
        });
      }} prefix="$" isFocused={focusOnLoad} />
        </div>}

      {!alt && !suggestedAmountOnly && <div className={cn(`Donate__items-${suggestedCount}`, {
      'Donate__other-input': state.otherSelected
    })}>
          <RadioGroup key={`${state.donateFrequency}-amounts`} name={`${state.donateFrequency}-donateAmount`} aria-label={t('donations:chooseAmount')} items={items_0} variant={variant === 'grid' ? 'grid' : 'buttons'} onChange={(e_1: React.ChangeEvent<HTMLInputElement>) => {
        if (e_1.target.value === 'other') {
          handleOtherAmountChange(state.otherAmounts[state.donateFrequency]);
        } else {
          handleAmountSelect(e_1.target.value);
        }
      }} value={state.selectedAmounts[state.donateFrequency].toString()} />
        </div>}
      {!alt && suggestedAmountOnly && <div className="Donate__other-suggestedAmount">
          <p className="label-potent pb-snug-xl">
            {state.suggestedAmounts?.[state.donateFrequency] && t('donations:suggestedGift', {
          amount: state.suggestedAmounts[state.donateFrequency]
        })}
          </p>
          <NumberField key={`${state.donateFrequency}-other`} inputRef={refs[state.donateFrequency]} name={state.donateFrequency} aria-label={t('donations:other')} placeholder="0" onChange={(e_2: React.ChangeEvent<HTMLInputElement>) => handleOtherAmountChange(e_2.target.value)} value={state.otherAmounts[state.donateFrequency]} onFocus={handleInputFocus} onBlur={handleInputBlur} prefix="$" isFocused={focusOnLoad} />
        </div>}
    </div>;
};